/**
 * Developer Tooling Functions for Module URL Override and External URL Override
 *
 * These functions enable developers to override module URLs and external URLs for applications
 * during development. Overrides are stored in sessionStorage and take precedence
 * over the dynamic module loading logic.
 *
 * These functions should NOT be used in production application code.
 */

import { AppConfig } from '@/types';

const MODULE_URL_OVERRIDE_PREFIX = 'psp.moduleUrlOverride.';
const EXTERNAL_URL_OVERRIDE_PREFIX = 'psp.externalUrlOverride.';
const OPEN_IN_NEW_TAB_OVERRIDE_PREFIX = 'psp.openInNewTabOverride.';

/**
 * Gets the module URL override for a given app basePath from sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @returns The module URL override, or null if not set
 */
export function getModuleUrlOverride(appBasePath: string): string | null {
  try {
    const key = `${MODULE_URL_OVERRIDE_PREFIX}${appBasePath}`;
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn(`[appRegistry] Failed to get module URL override for "${appBasePath}":`, error);
    return null;
  }
}

/**
 * Sets a module URL override for a given app basePath in sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @param moduleUrl - The module URL to override with, or null to clear the override
 */
export function setModuleUrlOverride(appBasePath: string, moduleUrl: string | null): void {
  try {
    const key = `${MODULE_URL_OVERRIDE_PREFIX}${appBasePath}`;
    if (moduleUrl === null || moduleUrl.trim() === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, moduleUrl.trim());
    }
  } catch (error) {
    console.warn(`[appRegistry] Failed to set module URL override for "${appBasePath}":`, error);
  }
}

/**
 * Clears all module URL overrides from sessionStorage.
 */
export function clearAllModuleUrlOverrides(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(MODULE_URL_OVERRIDE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn('[appRegistry] Failed to clear module URL overrides:', error);
  }
}

/**
 * Gets the external URL override for a given app basePath from sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @returns The external URL override, or null if not set
 */
export function getExternalUrlOverride(appBasePath: string): string | null {
  try {
    const key = `${EXTERNAL_URL_OVERRIDE_PREFIX}${appBasePath}`;
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn(`[appRegistry] Failed to get external URL override for "${appBasePath}":`, error);
    return null;
  }
}

/**
 * Sets an external URL override for a given app basePath in sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @param externalUrl - The external URL to override with, or null to clear the override
 */
export function setExternalUrlOverride(appBasePath: string, externalUrl: string | null): void {
  try {
    const key = `${EXTERNAL_URL_OVERRIDE_PREFIX}${appBasePath}`;
    if (externalUrl === null || externalUrl.trim() === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, externalUrl.trim());
    }
  } catch (error) {
    console.warn(`[appRegistry] Failed to set external URL override for "${appBasePath}":`, error);
  }
}

/**
 * Gets the openInNewTab override for a given app basePath from sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @returns The openInNewTab override value, or null if not set
 */
export function getOpenInNewTabOverride(appBasePath: string): boolean | null {
  try {
    const key = `${OPEN_IN_NEW_TAB_OVERRIDE_PREFIX}${appBasePath}`;
    const value = sessionStorage.getItem(key);
    if (value === null) return null;
    return value === 'true';
  } catch (error) {
    console.warn(`[appRegistry] Failed to get openInNewTab override for "${appBasePath}":`, error);
    return null;
  }
}

/**
 * Sets an openInNewTab override for a given app basePath in sessionStorage.
 *
 * @param appBasePath - The basePath of the app
 * @param openInNewTab - The openInNewTab value to override with, or null to clear the override
 */
export function setOpenInNewTabOverride(appBasePath: string, openInNewTab: boolean | null): void {
  try {
    const key = `${OPEN_IN_NEW_TAB_OVERRIDE_PREFIX}${appBasePath}`;
    if (openInNewTab === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, String(openInNewTab));
    }
  } catch (error) {
    console.warn(`[appRegistry] Failed to set openInNewTab override for "${appBasePath}":`, error);
  }
}

/**
 * Clears all external URL and openInNewTab overrides from sessionStorage.
 */
export function clearAllExternalUrlOverrides(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.startsWith(EXTERNAL_URL_OVERRIDE_PREFIX) ||
          key.startsWith(OPEN_IN_NEW_TAB_OVERRIDE_PREFIX))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn('[appRegistry] Failed to clear external URL overrides:', error);
  }
}

/**
 * Clears all overrides (module URL, external URL, and openInNewTab) from sessionStorage.
 */
export function clearAllOverrides(): void {
  clearAllModuleUrlOverrides();
  clearAllExternalUrlOverrides();
}

/**
 * DEV TOOL ONLY: Applies dev tool overrides to an app config.
 * Checks for module URL overrides, external URL overrides, and openInNewTab overrides,
 * and returns a modified copy of the app config with overridden properties applied.
 *
 * This function creates a duplicate of the app config and replaces properties that have
 * been overridden with values from sessionStorage.
 *
 * Priority order:
 * 1. Module URL override (takes precedence - converts app to remote app)
 * 2. External URL override (converts app to external URL app)
 *
 * TODO: Remove this entire function when moving to production.
 *
 * @param app - The app configuration from the registry
 * @returns A modified copy of the app config with overrides applied, or the original config if no overrides exist
 */
export function applyDevToolOverrides(app: AppConfig): AppConfig {
  const moduleUrlOverride = getModuleUrlOverride(app.basePath);
  const externalUrlOverride = getExternalUrlOverride(app.basePath);
  const openInNewTabOverride = getOpenInNewTabOverride(app.basePath);

  // If no overrides exist, return the original config
  if (!moduleUrlOverride && !externalUrlOverride && openInNewTabOverride === null) {
    return app;
  }

  // Create a duplicate of the app config
  const modifiedConfig: AppConfig = { ...app };

  // Apply module URL override (takes precedence over external URL override)
  if (moduleUrlOverride) {
    modifiedConfig.moduleRoutes = [
      {
        pattern: '/',
        resolver: moduleUrlOverride,
      },
    ];
    // Clear conflicting properties when module override is applied
    modifiedConfig.externalUrl = undefined;
    modifiedConfig.component = undefined;
  }
  // Apply external URL override (only if module override doesn't exist)
  else if (externalUrlOverride) {
    modifiedConfig.externalUrl = externalUrlOverride;
    // Apply openInNewTab override if it exists, otherwise default to false
    modifiedConfig.openInNewTab = openInNewTabOverride ?? false;
    // Clear conflicting properties when external URL override is applied
    modifiedConfig.moduleRoutes = undefined;
    modifiedConfig.component = undefined;
  }

  return modifiedConfig;
}
